<template>
  <v-form ref="form" class="pod-comments-form" data-cy="commentForm-form" v-on:keydown.esc="hideForm">
    <div v-if="isFirstLevelComment && !isImagePod" class="d-flex pod-comments-in-out justify-space-between">
      <div>
        <v-text-field
          v-model="inTime"
          v-bind:rules="inOutTimeInputRules"
          v-bind:placeholder="$t('routes.pod.menuComment.inTime')"
          v-bind:append-inner-icon="focusedInput === 'in' ? 'mdi-vector-link' : ''"
          v-on:focus="onInTimeFocus"
          v-on:click:append-inner="focusedInput = ''"
          v-on:keydown.esc="hideForm"
          density="compact"
          variant="solo-filled"
          block flat
          data-cy="commentForm-inTime"
        />
      </div>
      <div class="d-flex justify-space-around">
        <v-tooltip location="bottom" :aria-label="linkInOutTimeLabel">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="text"
              size="small"
              icon
              :aria-label="linkInOutTimeLabel"
              @click="inOutTimesLinked = !inOutTimesLinked"
            >
              <v-icon>{{ !inOutTimesLinked ? 'mdi-link' : 'mdi-link-off' }}</v-icon>
            </v-btn>
          </template>
          <span>{{ linkInOutTimeLabel }}</span>
        </v-tooltip>
      </div>
      <div>
        <v-text-field
          v-model="outTime"
          :rules="inOutTimeInputRules.concat(outTimeComparison)"
          :placeholder="$t('routes.pod.menuComment.outTime')"
          :append-inner-icon="focusedInput === 'out' ? 'mdi-vector-link' : ''"
          v-on:focus="onOutTimeFocus"
          v-on:click:append-inner="focusedInput = ''"
          v-on:keydown.esc="hideForm"
          flat
          block
          density="compact"
          variant="solo-filled"
          data-cy="commentForm-outTime"
        />
      </div>
    </div>


    <div class="d-flex">
      <div class="flex-grow-1">
        <v-textarea
          v-model="commentText"
          class="media-list-notes-textarea"
          :rules="commentTextRules"
          :error-messages="commentTextError"
          :placeholder="$t('routes.pod.menuComment.addComment')"
          @keydown.esc="hideForm"
          @update:model-value="onCommentInput"
          counter="2048"
          persistent-counter
          rows="3"
          variant="solo-filled"
          required
          flat
          data-cy="commentForm-text"
        />
      </div>

      <div class="ml-2 d-flex flex-column justify-center">

        <!-- Team only comments -->
        <v-tooltip
          v-if="$canBelongstoTeam()"
          location="bottom"
          z-index="1200"
          :aria-label="teamOnlyButtonLabel"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              v-on:click="teamonly = !teamonly"
              variant="text"
              size="small"
              rounded="10"
              :aria-label="teamOnlyButtonLabel"
              tile
              icon
            >
              <v-icon v-if="!teamonly" size="small" color="primary">
                mdi-lock-open-variant-outline
              </v-icon>
              <v-icon v-else size="small" color="primary">mdi-lock-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ teamOnlyButtonLabel }}</span>
        </v-tooltip>

        <!-- Save button -->
        <v-tooltip location="bottom" z-index="1200" :aria-label="$t('common.save')">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              v-bind:disabled="isSubmitted"
              v-on:click="saveComment"
              variant="text"
              size="small"
              rounded="10"
              :aria-label="$t('common.save')"
              data-cy="commentForm-save-btn"
              tile
              icon
            >
              <v-icon size="small" color="primary">mdi-floppy</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('common.save') }}</span>
        </v-tooltip>

        <!-- Cancel button -->
        <v-tooltip location="bottom" z-index="1200" :aria-label="$t('common.cancel')">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="text"
              size="small"
              rounded="10"
              :aria-label="$t('common.cancel')"
              v-on:click="hideForm"
              data-cy="commentForm-cancel-btn"
              tile
              icon
            >
              <v-icon size="small" color="primary">mdi-cancel</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('common.cancel') }}</span>
        </v-tooltip>
      </div>
    </div>
  </v-form>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { usePlayerStore } from '@/stores/PlayerStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import { usePodCommentStore } from '@/stores/PodCommentStore.js';
import { toTimeMilliseconds } from '@/components/formatters.js';

export default {
  emits: ["visibility"],
  name: 'PodCommentForm',

  props: {
    editComment: {
      type: Object,
      required: false,
    },
    replyComment: {
      type: Object,
      required: false,
    }
  },

  data: function() {
    return {
      commentText: "",
      commentTextError: "",
      commentTextRules: [
        v => !!v || this.$t('routes.pod.menuComment.commentIsRequired'),
      ],

      isSubmitted: false,
      inOutTimesLinked: false,
      teamonly: false,
      focusedInput: "",
      inTime: "",
      outTime: "",
      inOutTimeInputRules: [
        v => {
          if (!v) return true;
          if (!/^\d{2,}:\d{2}\.\d{2}$/g.test(v)) return this.$t('routes.pod.menuComment.invalidFormat');
          let seconds = v.split(".")[0].split(":")[1];
          if (parseInt(seconds) >= 60) return this.$t('routes.pod.menuComment.invalidSeconds');
          let videoDuration = (this.transcodedAsset && this.transcodedAsset.duration) ? parseFloat(this.transcodedAsset.duration) : 0;
          if (!videoDuration) return true;
          if ((this.videotimeToInt(v) / 1000) > videoDuration) return this.$t('routes.pod.menuComment.incorrectTime');
          return true;
        }
      ],
    };
  },

  mounted: function() {
    if (this.editComment) {
      this.commentText = this.editComment.content;
      this.teamonly = this.editComment.teamonly;

      if (this.editComment.start !== null) {
        this.inTime = toTimeMilliseconds(this.editComment.start);
      }

      if (this.editComment.end) {
        this.outTime = toTimeMilliseconds(this.editComment.end);
      }
    }

    if (this.replyComment) {
      if (this.replyComment.start !== null) {
      this.inTime = toTimeMilliseconds(this.replyComment.start);
      }

      if (this.replyComment.end) {
        this.outTime = toTimeMilliseconds(this.replyComment.end);
      }
    }

    if (!this.editComment && !this.replyComment) {
      this.inTime = toTimeMilliseconds(this.playerCURRENTTIME);
      this.outTime = "";
      // this.outTime = toTimeMilliseconds(this.playerCURRENTTIME + 2);
    }

    const podCommentStore = usePodCommentStore();
    podCommentStore.increaseVisibleForms();
  },

  beforeUnmount: function() {
    const podCommentStore = usePodCommentStore();
    podCommentStore.decreaseVisibleForms();
  },

  methods: {
    videotimeToInt: function(value) {
      let minutes = value.split(":")[0];
      let seconds = value.split(".")[0].split(":")[1];
      let milliseconds = value.split(".")[1];

      milliseconds = parseInt(parseFloat("0." + milliseconds) * 1000);
      seconds = parseInt(seconds) * 1000;
      minutes = parseInt(minutes) * 60000;
      return minutes + seconds + milliseconds;
    },
    hideForm: function() {
      this.$emit('visibility', false);
    },
    onCommentInput: function() {
      if (this.playerISPLAYING) {
        const playerStore = usePlayerStore();
        playerStore.setPauseEvent(this.playerCURRENTTIME);
      }
    },
    onInTimeFocus: function () {
      this.focusedInput = 'in';

      if (this.inTime && !this.playerISPLAYING) {
        const time = this.videotimeToInt(this.inTime) / 1000;
        const playerStore = usePlayerStore();
        playerStore.setSeekTimeEvent(time);
      }
    },
    onOutTimeFocus: function () {
      this.focusedInput = 'out';

      if (this.outTime && !this.playerISPLAYING) {
        const time = this.videotimeToInt(this.outTime) / 1000;
        const playerStore = usePlayerStore();
        playerStore.setSeekTimeEvent(time);
      }
    },
    updateFocusedInput: function(input) {
      this.focusedInput = input;
    },
    saveComment: async function() {
      const { valid } = await this.$refs.form.validate();
      this.commentTextError = "";
      if (!valid) {
        return;
      }

      if (this.isSubmitted) {
        return;
      }

      const podCommentStore = usePodCommentStore();
      let VERB = podCommentStore.createComment;

      const data = {
        podID: this.pod.id,
        payload: {
          start: this.inTime ? this.videotimeToInt(this.inTime) : null,
          end: this.outTime ? this.videotimeToInt(this.outTime) : null,
          content: this.commentText,
          teamonly: this.$canBelongstoTeam() ? this.teamonly : false,
          // parentid: // parent comment id
        }
      };

      if (this.isImagePod) {
        data.payload.start = null;
        data.payload.end = null;
      }

      if (this.replyComment) {
        data.payload.parentid = this.replyComment.id;
      }

      if (this.editComment) {
        data.commentID = this.editComment.id;
        data.payload.parentid = this.editComment.parentid;
        VERB = podCommentStore.updateComment;
      }

      this.isSubmitted = true;

      try {
        await VERB(data);
        this.hideForm();
        return podCommentStore.refreshComments(this.pod.id);
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          this.commentTextError = error.response.data.error.message;
        } else {
          this.commentTextError = "Error!";
          console.log(error);   // unhandled error
        }

        this.isSubmitted = false;
      }
    }
  },

  computed: {
    outTimeComparison: function () {
      if (this.outTime && (this.videotimeToInt(this.inTime) >= this.videotimeToInt(this.outTime))) {
        return this.$t('routes.pod.menuComment.invalidOutTime');
      }
      return true;
    },
    pod: function() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
    isImagePod: function () {
      const podStore = usePodStore();
      return podStore.isImagePod;
    },
    rawAsset: function() {
      if (this.pod &&
          this.pod.asset &&
          this.pod.asset.raw &&
          this.pod.asset.raw[0]) {
        return this.pod.asset.raw[0];
      }
      return null;
    },
    transcodedAsset: function() {
      if (!this.pod ||
          !this.pod.asset ||
          !this.pod.asset.web ||
          !Array.isArray(this.pod.asset.web)) {
        return null;
      }

      // Get first web asset with status COMPLETE
      for(let i = 0; i < this.pod.asset.web.length; i++) {
        if (this.pod.asset.web[i].state === this.getPodStates.COMPLETE) {
          return this.pod.asset.web[i];
        }
      }

      return null;
    },
    isFirstLevelComment: function() {
      if (!this.editComment && !this.replyComment) {
        return true;
      }

      return this.editComment && !this.editComment.parentid;
    },
    playerCURRENTTIME: function() {
      const playerStore = usePlayerStore();
      return playerStore.currentTime;
    },
    playerISPLAYING: function() {
      const playerStore = usePlayerStore();
      return playerStore.isPlaying;
    },
    getPodStates: function() {
      const commonStore = useCommonStore();
      return commonStore.podStates;
    },
    getCloseForms: function() {
      const podCommentStore = usePodCommentStore();
      return podCommentStore.getCloseFormsEvent;
    },
    teamOnlyButtonLabel: function() {
      return !this.teamonly
        ? this.$t('routes.pod.menuComment.makeCommentTeamonly')
        : this.$t('routes.pod.menuComment.makeCommentPublic');
    },
    linkInOutTimeLabel: function() {
      return !this.inOutTimesLinked
        ? this.$t('routes.pod.menuComment.linkInOutTime')
        : this.$t('routes.pod.menuComment.unlinkInOutTime');
    }
  },

  watch: {
    playerCURRENTTIME: function(newTime) {
      const addingNewComment = !this.editComment && !this.replyComment;

      if (this.focusedInput === "in" || (addingNewComment && this.focusedInput !== "out")) {
        this.inTime = toTimeMilliseconds(newTime);
        if (this.outTime && this.inOutTimesLinked) {
          this.outTime = toTimeMilliseconds(newTime + 2);
        }
      }

      if (this.focusedInput === "out") {
        this.outTime = toTimeMilliseconds(newTime);
      }
    },
    getCloseForms: function() {
      // close the form
      this.hideForm();
    },
  }
};
</script>

<style>
.pod-comments-form {
  padding: 10px;
  border-radius: 20px;
}

.v-theme--light .pod-comments-form {
  background-color: #FFFFFF;
}
.v-theme--dark .pod-comments-form {
  background-color: #232b36;
}

.pod-comments-in-out > div { width: 33%;}
</style>
